import { keyframes } from '@emotion/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ClaimStepper, Loading } from '@/core/components';
import { useRace } from '@/core/contexts/RaceContext';
import { useAuth } from '@/core/hooks/useAuth';
import { useClaimStatus } from '@/core/hooks/useClaim';
import {
  createClaim,
  getClaimByRunnerId,
  getExistingClaim,
  getRaceFromFirestore,
  getRunnerByTimingHash,
  getUserData,
} from '@/core/services/claim';
import { getNftMediaUrl } from '@/core/services/storage';
import type { Claim, Runner } from '@/core/types';
import { calcNetTime, getFinishTime, getStartTime } from '@/core/types';
import { BibCheckStep, ClaimStatusStep, IdentityVerificationStep, ResultConfirmStep } from './components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 紙吹雪が画面上部から下部まで降ってくる
const confettiFall = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh) rotate(var(--rot));
    opacity: 0;
  }
`;

// 紙吹雪の色
const confettiColors = ['#FF8800', '#6A2BFF', '#FFD700', '#FF4081', '#00BCD4', '#4CAF50'];

// 紙吹雪の生成
const generateFallingConfetti = (count: number) => {
  return Array.from({ length: count }, (_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 5;
    const duration = 4 + Math.random() * 4;
    const size = 6 + Math.random() * 8;
    const rotation = (Math.random() - 0.5) * 720;
    const color = confettiColors[Math.floor(Math.random() * confettiColors.length)];
    return { left, delay, duration, size, rotation, color, id: i };
  });
};

type Step = 'loading' | 'bib-check' | 'identity-verification' | 'result-confirm' | 'claiming' | 'need-inquiry';

export function ClaimPage() {
  const { raceAlias } = useParams<{ raceAlias: string }>();
  const { user, loading: authLoading } = useAuth();
  const { race } = useRace();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const timingHash = searchParams.get('code');
  const mintContext = timingHash ? 'on_site' : 'online';

  const [step, setStep] = useState<Step>('loading');
  const [bib, setBib] = useState<string>('');
  const [runner, setRunner] = useState<Runner | null>(null);
  const [existingClaim, setExistingClaim] = useState<Claim | null>(null);
  const [runnerClaim, setRunnerClaim] = useState<Claim | null>(null);
  const [claimId, setClaimId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firestoreRace, setFirestoreRace] = useState<{ id: string; data: Record<string, unknown> } | null>(null);
  const [nftMediaUrl, setNftMediaUrl] = useState<string | null>(null);

  // ステータス監視（onSnapshotでリアルタイム更新）
  const { claim } = useClaimStatus({ claimId });

  // 紙吹雪を生成（初回のみ）- ログインページと同じ160個
  const confetti = useMemo(() => generateFallingConfetti(160), []);

  // 初期化処理
  const initialize = useCallback(async () => {
    if (!user || authLoading) return;

    setStep('loading');
    setError(null);

    try {
      // Firestoreから大会情報を取得
      const fsRace = await getRaceFromFirestore(race.alias);
      if (!fsRace) {
        setError('大会が見つかりません');
        return;
      }
      setFirestoreRace(fsRace);

      // 既存のclaimがあるか確認
      const existing = await getExistingClaim(user.uid, fsRace.id);
      if (existing) {
        setExistingClaim(existing);
        setClaimId(existing.id);
        setStep('claiming');
        return;
      }

      // timingHashがある場合は直接結果を取得
      if (timingHash) {
        const runnerResult = await getRunnerByTimingHash(timingHash);
        if (runnerResult) {
          setRunner(runnerResult);
          setBib(runnerResult.bib);
          // このランナーのclaim状態を確認
          const existingRunnerClaim = await getClaimByRunnerId(runnerResult.id);
          setRunnerClaim(existingRunnerClaim);
          setStep('result-confirm');
          return;
        }
        setError('結果が見つかりません。URLを確認してください。');
        return;
      }

      // 本人確認フローへ
      setStep('bib-check');
    } catch (err) {
      console.error('Initialization error:', err);
      setError('初期化中にエラーが発生しました');
    }
  }, [user, authLoading, race.alias, timingHash]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // 動画生成完了後にNFTメディアURLを取得
  // fileName があれば動画はできている（ステータス関係なく表示）
  useEffect(() => {
    const activeClaim = claim ?? existingClaim;
    if (activeClaim?.fileName) {
      getNftMediaUrl({
        fileName: activeClaim.fileName,
        raceAlias: activeClaim.raceAlias,
      })
        .then(setNftMediaUrl)
        .catch(console.error);
    }
  }, [claim, existingClaim]);

  // ゼッケン確認成功
  const handleBibCheckSuccess = (checkedBib: string) => {
    setBib(checkedBib);
    setStep('identity-verification');
  };

  // 本人確認成功
  const handleIdentitySuccess = async (verifiedRunner: Runner) => {
    setRunner(verifiedRunner);
    // このランナーのclaim状態を確認
    const existingRunnerClaim = await getClaimByRunnerId(verifiedRunner.id);
    setRunnerClaim(existingRunnerClaim);
    setStep('result-confirm');
  };

  // 受け取り処理
  const handleClaim = async (destination?: string) => {
    if (!user || !runner || !firestoreRace) return;

    // ユーザーのウォレットアドレスを取得
    const userData = await getUserData(user.uid);

    // ウォレットアドレスがない場合は少し待って再取得
    let walletAddress = userData?.walletAddress;
    if (!walletAddress) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      const refreshedUserData = await getUserData(user.uid);
      walletAddress = refreshedUserData?.walletAddress;
    }

    const raceData = firestoreRace.data;
    const startTime = getStartTime(runner);
    const finishTime = getFinishTime(runner);
    const netTime = calcNetTime(startTime, finishTime);

    const newClaimId = await createClaim({
      uid: user.uid,
      raceId: firestoreRace.id,
      raceAlias: race.alias,
      raceName: String(raceData.raceName || race.name),
      raceDate: String(raceData.raceDate || ''),
      bib: runner.bib,
      startTime,
      time: finishTime,
      netTime,
      runnerId: runner.id,
      mintContext,
      destination: destination || walletAddress || null,
      chain: String(raceData.chain || race.mint?.chain || 'soneium'),
      contractAddress: String(raceData.contractAddress || race.mint?.contractAddress || ''),
      tokenName: String(raceData.tokenName || raceData.contractName || ''),
    });

    setClaimId(newClaimId);
    setStep('claiming');
  };

  // 認証待ち
  if (authLoading) {
    return <Loading />;
  }

  // 未ログイン → ログインページへリダイレクト
  if (!user) {
    sessionStorage.setItem('login_return_url', window.location.pathname + window.location.search);
    return <Navigate to={`/${raceAlias}`} replace />;
  }

  // ステッパーのアクティブステップを計算
  // 0: ログイン（ClaimPageに来た時点で完了）, 1: 認証, 2: 完了
  const getActiveStep = () => {
    if (['loading', 'bib-check', 'identity-verification', 'result-confirm', 'need-inquiry'].includes(step)) return 1;
    // claiming ステップ: 完了状態かどうかで判定
    const activeClaim = claim ?? existingClaim;
    const isCompleted = activeClaim?.status === 'completed';
    return isCompleted ? 2 : 1;
  };

  return (
    <>
      {/* 紙吹雪 - result-confirm ステップで表示 */}
      {step === 'result-confirm' && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          {confetti.map((c) => (
            <Box
              key={c.id}
              sx={{
                position: 'absolute',
                top: 0,
                left: `${c.left}%`,
                width: c.size,
                height: c.size * 0.6,
                backgroundColor: c.color,
                borderRadius: '2px',
                '--rot': `${c.rotation}deg`,
                animation: `${confettiFall} ${c.duration}s linear ${c.delay}s infinite`,
              }}
            />
          ))}
        </Box>
      )}

      {/* ステッパー（モバイルは狭め） */}
      <Box
        sx={{
          mt: { xs: 2, sm: 5 },
          mb: { xs: 2, sm: 5 },
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <ClaimStepper activeStep={getActiveStep()} />
      </Box>

      {/* タイトルエリア */}
      <Box
        sx={{
          textAlign: 'center',
          mb: { xs: 3, sm: 5 },
          animation: `${fadeInUp} 0.6s ease-out 0.1s both`,
        }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            letterSpacing: '0.2em',
            color: 'text.secondary',
            mb: 1,
            fontSize: '0.7rem',
          }}
        >
          RECEIVE YOUR CERTIFICATE
        </Typography>
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            background: gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          デジタル完走証の受け取り
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            animation: `${fadeInUp} 0.6s ease-out`,
          }}
        >
          {error}
        </Alert>
      )}

      {/* ステップコンテンツ */}
      <Box
        sx={{
          animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
        }}
      >
        {step === 'loading' && <Loading />}

        {step === 'bib-check' && firestoreRace && (
          <BibCheckStep
            raceId={firestoreRace.id}
            onSuccess={handleBibCheckSuccess}
            onNeedInquiry={() => setStep('need-inquiry')}
          />
        )}

        {step === 'identity-verification' && firestoreRace && (
          <IdentityVerificationStep
            raceId={firestoreRace.id}
            bib={bib}
            onSuccess={handleIdentitySuccess}
            onBack={() => setStep('bib-check')}
          />
        )}

        {step === 'result-confirm' && runner && (
          <ResultConfirmStep
            runner={runner}
            isAlreadyIssued={runnerClaim?.status === 'completed'}
            onClaim={handleClaim}
          />
        )}

        {(() => {
          const activeClaim = claim ?? existingClaim;
          return step === 'claiming' && activeClaim ? (
            <ClaimStatusStep claim={activeClaim} nftMediaUrl={nftMediaUrl ?? undefined} />
          ) : null;
        })()}

        {step === 'need-inquiry' && (
          <Alert severity="info">
            本人確認に必要な情報が登録されていません。
            <br />
            お手数ですが、
            <a href="mailto:contact@swandive.co.jp">contact@swandive.co.jp</a>
            までお問い合わせください。
          </Alert>
        )}
      </Box>
    </>
  );
}
